/**
 * Column definitions for participants table.
 * Displays participant details, ratings, sign-in status, events, and teams.
 * Rating columns are generated dynamically from participant data.
 */
import { participantProfileModal } from 'components/modals/participantProfileModal';
import { formatParticipant } from '../common/formatters/participantFormatter';
import { genderConstants, participantRoles } from 'tods-competition-factory';
import { arrayLengthFormatter } from '../common/formatters/arrayLength';
import { contactFormatter } from '../common/formatters/contactFormatter';
import { participantSorter } from '../common/sorters/participantSorter';
import { participantActions } from '../../popovers/participantActions';
import { eventsFormatter } from '../common/formatters/eventsFormatter';
import { teamsFormatter } from '../common/formatters/teamsFormatter';
import { roleBadgeFormatter } from '../common/formatters/roleBadge';
import { applyColumnVisibility } from '../common/columnIsVisible';
import { getRatingColumns } from '../common/getRatingColumns';
import { navigateToEvent } from '../common/navigateToEvent';
import { tournamentEngine } from 'services/factory/engine';
import { threeDots } from '../common/formatters/threeDots';
import { toggleSignInStatus } from './toggleSignInStatus';
import { idEditor } from '../common/editors/idEditor';
import { headerMenu } from '../common/headerMenu';
import { t } from 'i18n';

// constants
import { CENTER, LEFT, PARTICIPANTS, RIGHT, STAFF } from 'constants/tmxConstants';
import { context } from 'services/context';

const { FEMALE, MALE } = genderConstants;
const { OFFICIAL } = participantRoles;

/** Numeric-aware sort for the Jersey # column. Empty / non-numeric strings
 *  sink to the bottom so the column reads top-to-bottom by jersey number. */
function jerseySorter(a: any, b: any): number {
  const na = a == null || a === '' ? Number.POSITIVE_INFINITY : Number(a);
  const nb = b == null || b === '' ? Number.POSITIVE_INFINITY : Number(b);
  const ax = Number.isFinite(na) ? na : Number.POSITIVE_INFINITY;
  const bx = Number.isFinite(nb) ? nb : Number.POSITIVE_INFINITY;
  if (ax === bx) return 0;
  return ax - bx;
}

export function getParticipantColumns({
  data,
  view,
  replaceTableData,
}: {
  data: any[];
  view?: string;
  replaceTableData: () => void;
}): any[] {
  // Shown by default for personnel, hidden by default for competitors (CA). A competitor's contact is
  // TD-scope information — the privacy policy strips it from every public surface — so the column would
  // be a row of empty cells on the view that has the most rows. Personnel are the population whose
  // contacts can actually be published, so it earns its place there. Either way it stays in the header
  // menu, so a TD can turn it on for competitors when chasing alternates.
  const isPersonnelView = view === OFFICIAL || view === STAFF;
  const cityState = data.some((p) => p.cityState);
  const tennisId = data.some((p) => p.tennisId);
  const jerseyNumberPresent = data.some((p) => p.jerseyNumber);
  const teamAffiliationPresent = data.some((p) => p.teamAffiliation);
  const rolePresent = data.some((p) => p.participantRole);
  const ratingColumns = getRatingColumns(data, 'participant');

  return applyColumnVisibility([
    {
      cellClick: (_: Event, cell: any) => cell.getRow().toggleSelect(),
      titleFormatter: 'rowSelection',
      formatter: 'rowSelection',
      headerSort: false,
      responsive: false,
      hozAlign: LEFT,
      width: 5,
    },
    {
      headerMenu: headerMenu({
        signedIn: 'Sign In Status',
        contacts: 'Contact',
        contactPublic: 'Public contact',
        sex: 'Gender',
      }),
      formatter: 'rownum',
      headerSort: false,
      headerHozAlign: CENTER,
      hozAlign: CENTER,
      width: 65,
    },
    {
      formatter: formatParticipant(
        (params: any) => {
          const clickedParticipant = params?.individualParticipant || params?.participant;
          const rowData = params?.cell?.getRow().getData();
          if (!rowData) return;
          const { participantType } = rowData;
          if (participantType !== 'INDIVIDUAL') return;
          const participantId = clickedParticipant?.participantId || rowData.participantId;
          if (!participantId) return;
          const table = params?.cell?.getTable();
          if (!table) return;
          const participantIds = (table.getData() as any[])
            .filter((r: any) => r.participantType === 'INDIVIDUAL')
            .map((r: any) => r.participantId);
          participantProfileModal({ participantId, participantIds });
        },
        { useParticipantName: true },
      ),
      sorter: participantSorter,
      field: 'participant',
      responsive: false,
      resizable: false,
      minWidth: 200,
      widthGrow: 2,
      title: t('tables.participants.name'),
    },
    {
      // Jersey number lives on `person.biographicalInformation.teamAttributes[0]
      // .jerseyNumber`. Surfaced for Competitors view (and any other view) when
      // any row has one — auto-hides for sports / tournaments that don't track
      // them. Pre-sorts top-to-bottom so the roster reads in jersey order.
      title: '#',
      field: 'jerseyNumber',
      visible: jerseyNumberPresent,
      sorter: jerseySorter,
      headerSort: true,
      hozAlign: CENTER,
      width: 60,
      formatter: (cell: any) => {
        const v = cell.getValue();
        return v == null || v === '' ? '' : `<strong>${v}</strong>`;
      },
    },
    {
      // Per-row `participantRole` badge. Critical for the Staff view where
      // every row is a different non-COMPETITOR role; hidden by default
      // elsewhere because Competitors are all COMPETITOR by definition.
      title: t('tables.participants.role'),
      field: 'participantRole',
      visible: rolePresent,
      formatter: roleBadgeFormatter,
      headerSort: true,
      width: 130,
    },
    {
      // Team affiliation from `teamAttributes[0].teamName` — the import wizard
      // populates this on every imported person regardless of role. Visible
      // when any row has a value so the column appears in Staff view (where
      // it's essential) and Competitors view (where it shows the imported
      // team affiliation pre-team-generation). Sortable but no header sort
      // icon — matches the visual treatment used by the existing `teams`
      // column on the same table (see `headerSortElement` exclude list in
      // `createParticipantsTable.ts`).
      title: t('tables.participants.teamAffiliation'),
      field: 'teamAffiliation',
      visible: teamAffiliationPresent,
      headerSort: true,
      minWidth: 140,
    },
    {
      editor: idEditor({ field: 'tennisId' }),
      visible: tennisId,
      field: 'tennisId',
      editable: false,
      title: t('tables.participants.wtid'),
      width: 120,
    },
    {
      headerFilter: 'input',
      title: t('tables.participants.firstName'),
      field: 'firstName',
      visible: false,
      width: 150,
    },
    {
      headerFilter: 'input',
      title: t('tables.participants.lastName'),
      field: 'lastName',
      visible: false,
      width: 150,
    },
    {
      formatter: (cell: any, formatterParams: any, onRendered: any) => {
        if (!cell.getValue()) return '';
        return formatParticipant((params: any) => {
          const clickedParticipant = params?.individualParticipant || params?.participant;
          const rowData = params?.cell?.getRow().getData();
          if (!rowData) return;
          const { participantType } = rowData;
          if (participantType !== 'INDIVIDUAL') return;
          const participantId = clickedParticipant?.participantId || rowData.participantId;
          if (!participantId) return;
          const table = params?.cell?.getTable();
          if (!table) return;
          const participantIds = (table.getData() as any[])
            .filter((r: any) => r.participantType === 'INDIVIDUAL')
            .map((r: any) => r.participantId);
          participantProfileModal({ participantId, participantIds });
        })(cell, formatterParams, onRendered);
      },
      title: t('tables.participants.nickname'),
      visible: data.some((p) => p.nickname),
      field: 'nickname',
      width: 150,
    },
    {
      title: '<i class="fa-solid fa-venus-mars" />',
      formatter: (cell: any) => {
        const value = cell.getValue();
        if (value === 'Male') return t('pages.participants.gender.male');
        if (value === 'Female') return t('pages.participants.gender.female');
        return value || t('pages.participants.gender.unknown');
      },
      field: 'sex',
      hozAlign: LEFT,
      editor: 'list',
      width: 80,
      editorParams: {
        itemFormatter: (_: any, value: any) => value,
        values: {
          [t('pages.participants.gender.male')]: MALE,
          [t('pages.participants.gender.female')]: FEMALE,
          '': t('pages.participants.gender.unknown'),
        },
      },
    },
    { title: t('tables.participants.country'), field: 'ioc', width: 130, visible: false, headerFilter: 'input' },
    {
      title: `<div class='fa-solid fa-check' style='color: var(--tmx-accent-green)' />`,
      cellClick: toggleSignInStatus,
      formatter: 'tickCross',
      resizable: false,
      field: 'signedIn',
      hozAlign: LEFT,
      tooltip: false,
      width: 40,
    },
    {
      // The call sheet, one row at a time — `tel:` / `sms:` / `mailto:` on the participant's primary
      // contact. Same default as the public-contact column beside it: shown for personnel, in the
      // header menu everywhere, because a director chasing an alternate wants it on competitors too.
      //
      // Not sortable. Sorting people by the shape of their phone number answers no question, and a
      // header sort arrow that produces an arbitrary order reads as a broken column.
      title: `<div class='fa-solid fa-address-book' style='color: var(--tmx-text-secondary)' />`,
      headerTooltip: t('tables.participants.contactActions'),
      formatter: contactFormatter,
      visible: isPersonnelView,
      headerHozAlign: CENTER,
      field: 'contacts',
      hozAlign: CENTER,
      headerSort: false,
      resizable: false,
      tooltip: false,
      width: 92,
    },
    {
      // Whether this person's primary contact carries consent to be shared publicly.
      //
      // Read-only. The toggle lives in the participant drawer, where it sits next to the number it
      // governs — a bare tick in a table row would not say WHICH contact it applied to, and a person can
      // hold several. Three states are distinguished on purpose: consented, has a contact but has not
      // consented, and has no contact at all. Collapsing the last two would make "nobody has opted in"
      // look identical to "nobody has a phone number", which are different problems for a TD.
      title: `<div class='fa-solid fa-share-nodes' style='color: var(--tmx-text-secondary)' />`,
      headerTooltip: t('tables.participants.contactPublic'),
      formatter: (cell: any) => {
        const row = cell.getRow().getData();
        if (!row.hasContact) return '';
        return cell.getValue()
          ? `<span class="tmx-contact-public" title="${t('tables.participants.contactPublicYes')}">✓</span>`
          : `<span class="tmx-contact-private" title="${t('tables.participants.contactPublicNo')}">—</span>`;
      },
      visible: isPersonnelView,
      field: 'contactPublic',
      headerHozAlign: CENTER,
      hozAlign: CENTER,
      resizable: false,
      headerSort: true,
      tooltip: false,
      width: 40,
    },
    {
      sorter: (a: any, b: any) => (a.length || 0) - (b?.length || 0),
      formatter: arrayLengthFormatter,
      title: t('tables.participants.penalties'),
      field: 'penalties',
      hozAlign: LEFT,
      visible: false,
      width: 130,
    },
    {
      headerFilter: 'input',
      title: t('tables.participants.club'),
      field: 'club',
      hozAlign: LEFT,
      minWidth: 70,
      editor: false,
      visible: false,
    },
    {
      sorter: (a: any, b: any) => a?.[0]?.eventName?.localeCompare(b?.[0]?.eventName, undefined, { numeric: true }),
      formatter: eventsFormatter(navigateToEvent),
      title: t('tables.participants.events'),
      field: 'events',
      hozAlign: LEFT,
      minWidth: 200,
      editor: false,
      widthGrow: 2,
    },
    {
      sorter: (a: any, b: any) => a?.[0]?.participantName?.localeCompare(b?.[0]?.participantName),
      formatter: teamsFormatter(() => {
        const tournamentId = tournamentEngine.q.tournament()?.tournamentId;
        if (tournamentId) context.router?.navigate(`/tournament/${tournamentId}/${PARTICIPANTS}/TEAM`);
      }),
      title: t('tables.participants.teams'),
      field: 'teams',
      hozAlign: LEFT,
      minWidth: 200,
      editor: false,
      widthGrow: 2,
    },
    {
      visible: !!cityState,
      title: t('tables.participants.cityState'),
      field: 'cityState',
      minWidth: 110,
    },
    ...ratingColumns,
    {
      cellClick: participantActions(replaceTableData),
      formatter: threeDots,
      responsive: false,
      headerSort: false,
      hozAlign: RIGHT,
      maxWidth: 40,
    },
  ]);
}
